import type { Priority, ActionStatus, AdPlatform, InvestigationStatus, ManagerOutcome } from './types'

/** Formata uma data (ISO ou date-only) como DD/MM/AAAA, sem deslocamento de fuso. */
export function formatDateBR(value: string | null | undefined): string {
  if (!value) return '—'
  const datePart = value.slice(0, 10) // YYYY-MM-DD
  const [y, m, d] = datePart.split('-')
  if (y && m && d) return `${d}/${m}/${y}`
  const parsed = new Date(value)
  return Number.isNaN(parsed.getTime()) ? '—' : parsed.toLocaleDateString('pt-BR')
}

/** Data + hora local (para campos de timestamp como generatedAt). */
export function formatDateTimeBR(value: string | null | undefined): string {
  if (!value) return '—'
  const d = new Date(value)
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('pt-BR')
}

export const PRIORITY_LABEL: Record<Priority, string> = {
  high: 'Alta',
  medium: 'Média',
  low: 'Baixa',
}

export const PRIORITY_CLASSES: Record<Priority, string> = {
  high: 'bg-red-100 text-red-700 border-red-200',
  medium: 'bg-amber-100 text-amber-700 border-amber-200',
  low: 'bg-slate-100 text-slate-600 border-slate-200',
}

const CATEGORY_LABEL: Record<string, string> = {
  budget: 'Orçamento',
  creative: 'Criativo',
  targeting: 'Segmentação',
  bid: 'Lance',
  other: 'Outro',
  cost: 'Custo',
  performance: 'Performance',
  engagement: 'Engajamento',
  conversion: 'Conversão',
}

export function categoryLabel(category: string): string {
  return CATEGORY_LABEL[category] ?? category
}

export const ACTION_STATUS_LABEL: Record<ActionStatus, string> = {
  pending: 'Pendente',
  done: 'Feito',
  skipped: 'Não fiz',
  done_differently: 'Fiz diferente',
  review: 'Rever em',
}

export const ACTION_STATUS_CLASSES: Record<ActionStatus, string> = {
  pending: 'bg-slate-100 text-slate-600',
  done: 'bg-emerald-100 text-emerald-700',
  skipped: 'bg-rose-100 text-rose-700',
  done_differently: 'bg-indigo-100 text-indigo-700',
  review: 'bg-orange-100 text-orange-700',
}

export const ACTION_STATUS_OPTIONS: ActionStatus[] = [
  'pending',
  'done',
  'skipped',
  'done_differently',
  'review',
]

// Motivo em 1 clique de "Não fiz"/"Fiz diferente" — espelha src/lib/actionState.ts.
// É o que diz à IA se a ação não serve para o cliente ("Discordo") ou se só não
// coube na semana ("Sem tempo").
export const ACTION_REASON_LABEL: Record<string, string> = {
  client_declined: 'Cliente não aprovou',
  no_budget: 'Sem verba',
  already_done: 'Já feito / já testado',
  disagree: 'Discordo do diagnóstico',
  context_changed: 'Contexto mudou',
  no_time: 'Sem tempo esta semana',
  adjusted: 'Ajustei valor ou escopo',
  other_campaign: 'Fiz em outra campanha',
  other_action: 'Fiz outra ação no lugar',
}

export const ACTION_REASONS_BY_STATUS: Partial<Record<ActionStatus, string[]>> = {
  skipped: ['client_declined', 'no_budget', 'already_done', 'disagree', 'context_changed', 'no_time'],
  done_differently: ['adjusted', 'other_campaign', 'other_action', 'client_declined'],
}

/** "Não fiz" exige motivo (1 clique) — é o status que mais ensina a IA. */
export const REASON_REQUIRED: ActionStatus[] = ['skipped']

export const MANAGER_OUTCOME_LABEL: Record<ManagerOutcome, string> = {
  confirmed: 'Confirmei a hipótese',
  refuted: 'Descartei a hipótese',
  unverified: 'Não consegui verificar',
}

export const PLATFORM_LABEL: Record<AdPlatform, string> = {
  google_ads: 'Google Ads',
  meta_ads: 'Meta Ads',
}

const DAYS = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado']
export function dayOfWeekLabel(day: number): string {
  return DAYS[day] ?? String(day)
}

// Agendamento é armazenado em UTC; a UI mostra/edita em horário de São Paulo
// (UTC−3, sem horário de verão no Brasil desde 2019). A conversão pode virar o
// dia da semana.
const SP_OFFSET = -3

export function utcToSp(dayUtc: number, hourUtc: number): { day: number; hour: number } {
  let hour = hourUtc + SP_OFFSET
  let day = dayUtc
  if (hour < 0) {
    hour += 24
    day = (day - 1 + 7) % 7
  } else if (hour >= 24) {
    hour -= 24
    day = (day + 1) % 7
  }
  return { day, hour }
}

export function spToUtc(daySp: number, hourSp: number): { day: number; hour: number } {
  let hour = hourSp - SP_OFFSET // +3
  let day = daySp
  if (hour >= 24) {
    hour -= 24
    day = (day + 1) % 7
  } else if (hour < 0) {
    hour += 24
    day = (day - 1 + 7) % 7
  }
  return { day, hour }
}

/** Rótulo do agendamento em horário de São Paulo (ex.: "Segunda, 06h"). */
export function scheduleLabelSP(dayUtc: number | null, hourUtc: number | null): string {
  if (dayUtc == null || hourUtc == null) return 'Sem agendamento'
  const sp = utcToSp(dayUtc, hourUtc)
  return `${dayOfWeekLabel(sp.day)}, ${String(sp.hour).padStart(2, '0')}h`
}

export function investigationStatusLabel(status: InvestigationStatus): string {
  switch (status) {
    case 'pending':
      return 'Pendente'
    case 'ai_running':
      return 'Investigando…'
    case 'ai_complete':
      return 'Concluída'
    case 'ai_error':
      return 'Falhou'
    case 'manager_pending':
      return 'Aguardando gerente'
    case 'manager_complete':
      return 'Concluída'
    default:
      return status
  }
}
